import React, { useEffect, useState, useCallback } from "react";
import { Search, PlusCircle, MinusCircle, Award, UserPlus, UsersRound, FileUp, Pencil, KeyRound, Ban, CheckCircle2, FolderPlus, FolderCog, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../../api";
import { useLang } from "../../i18n";
import { useConfig } from "../../context/ConfigContext";
import Avatar from "../../components/Avatar";
import { EmptyState, Modal, SkeletonPanel } from "../../components/common";
import AwardPointsModal from "../../components/AwardPointsModal";
import DeductPointsModal from "../../components/DeductPointsModal";
import AwardBadgeModal from "../../components/AwardBadgeModal";
import AddStudentModal from "../../components/AddStudentModal";
import BulkAddStudentsModal from "../../components/BulkAddStudentsModal";
import StudentImportModal from "../../components/StudentImportModal";
import EnableStudentLoginModal from "../../components/EnableStudentLoginModal";
import DeleteStudentModal from "../../components/DeleteStudentModal";
import { fmtRelative, wsLabel } from "../../utils";
import { toast } from "sonner";

const AVATARS = ["sara", "nora", "reem", "joud", "lamar"].map((n) => `/assets/avatars/${n}.jpeg`);
const studentTestKey = (student) => student.username || student.id;

function ClassFormModal({ open, onClose, onDone, editing }) {
  const { t } = useLang();
  const [nameAr, setNameAr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setNameAr(editing?.name_ar || "");
      setNameEn(editing?.name_en || "");
    }
  }, [open, editing]);

  const submit = async () => {
    if (!nameAr.trim()) { toast.error(t("validation.required")); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/staff/classes/${editing.id}`, { name_ar: nameAr, name_en: nameEn });
      else await api.post("/staff/classes", { name_ar: nameAr, name_en: nameEn });
      toast.success(editing ? t("teacher.class_renamed") : t("teacher.class_created"));
      onClose();
      onDone?.();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? t("teacher.rename_class") : t("teacher.create_class")} testId="class-form-modal">
      <div className="space-y-3">
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.class_name_ar")} *</label>
          <input data-testid="class-name-ar" className="input-magic" dir="rtl" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.class_name_en")}</label>
          <input data-testid="class-name-en" className="input-magic" dir="ltr" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
        </div>
        <button data-testid="class-save-btn" disabled={busy} onClick={submit} className="btn-magic w-full">
          {busy ? t("common.loading") : t("common.save")}
        </button>
      </div>
    </Modal>
  );
}

function EditStudentModal({ open, onClose, onDone, student, classes }) {
  const { t, pick } = useLang();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && student) {
      setForm({ name_ar: student.name_ar || "", name_en: student.name_en || "", class_id: student.class_id || "", avatar: student.avatar || AVATARS[0] });
    }
  }, [open, student]);

  if (!form) return null;
  const submit = async () => {
    if (!form.name_ar.trim()) { toast.error(t("validation.required")); return; }
    setBusy(true);
    try {
      await api.patch(`/staff/students/${student.id}`, {
        name_ar: form.name_ar, name_en: form.name_en,
        class_id: form.class_id || null, avatar: form.avatar,
      });
      toast.success(t("teacher.student_updated"));
      onClose();
      onDone?.();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`${t("teacher.edit_student")}: ${pick(student, "name")}`} testId="edit-student-modal">
      <div className="space-y-3">
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.full_name_ar")} *</label>
          <input data-testid="edit-student-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.full_name_en")}</label>
          <input data-testid="edit-student-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.move_to_class")}</label>
          <select data-testid="edit-student-class" className="input-magic" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
            <option value="" label="—" />
            {classes.map((c) => <option key={c.id} value={c.id} label={`${c.name_ar} — ${c.name_en}`} />)}
          </select>
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("teacher.avatar")}</label>
          <div className="flex gap-2 flex-wrap">
            {AVATARS.map((a) => (
              <button key={a} type="button" data-testid={`avatar-option-${a.split("/").pop()}`} onClick={() => setForm({ ...form, avatar: a })}
                className={`rounded-full transition-all ${form.avatar === a ? "ring-4 ring-[#FFC83D] scale-105 glow-gold" : "opacity-60 hover:opacity-100"}`}>
                <img src={a} alt="" className="w-12 h-12 rounded-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        <button data-testid="edit-student-save" disabled={busy} onClick={submit} className="btn-magic w-full">
          {busy ? t("common.loading") : t("common.save")}
        </button>
      </div>
    </Modal>
  );
}

export default function StudentsPage() {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [students, setStudents] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [preset, setPreset] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [loginStudent, setLoginStudent] = useState(null);

  const loadClasses = useCallback(async () => {
    const { data } = await api.get("/staff/classes");
    setClasses(data.classes.filter((c) => !c.archived));
  }, []);

  const load = useCallback(async () => {
    const params = { search };
    if (classFilter) params.class_id = classFilter;
    const { data } = await api.get("/staff/students", { params });
    setStudents(data.students);
    const visibleActiveIds = new Set(
      data.students
        .filter((student) => student.is_active)
        .map((student) => student.id)
    );
    setSelected(
      (current) => new Set([...current].filter((id) => visibleActiveIds.has(id)))
    );
  }, [search, classFilter]);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectVisible = () => {
    const activeIds = (students || [])
      .filter((student) => student.is_active)
      .map((student) => student.id);
    setSelected(new Set(activeIds));
  };

  const clearSelection = () => setSelected(new Set());

  const open = (m, ids = []) => { setPreset(ids); setModal(m); };

  const toggleLogin = async (s) => {
    try {
      await api.patch(`/staff/students/${s.id}/login`, { login_active: !s.login_active });
      toast.success(s.login_active ? t("teacher.login_disabled") : t("teacher.login_reactivated"));
      load();
    } catch (e) { toast.error(apiError(e, t)); }
  };

  const resetPw = async (s) => {
    try {
      const { data } = await api.post(`/staff/students/${s.id}/reset-password`);
      setTempPw({ name: pick(s, "name"), pw: data.temp_password });
      toast.success(t("teacher.password_reset_done"));
    } catch (e) { toast.error(apiError(e, t)); }
  };

  const selectedClass = classes.find((c) => c.id === classFilter);

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl sm:text-3xl font-black flex-1">{wsLabel(config?.ui_labels, "students", lang, t("nav.students"))}</h2>
        <select data-testid="class-filter" className="input-magic !w-auto text-sm py-2" value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}>
          <option value="" label={t("teacher.all_classes")} />
          {classes.map((c) => <option key={c.id} value={c.id} label={`${c.name_ar} (${c.student_count})`} />)}
        </select>
        <button data-testid="create-class-btn" onClick={() => { setEditingClass(null); setModal("class"); }} className="btn-magic btn-cyan text-sm">
          <FolderPlus className="w-4 h-4" /> {t("teacher.create_class")}
        </button>
        {selectedClass && (
          <button data-testid="rename-class-btn" onClick={() => { setEditingClass(selectedClass); setModal("class"); }} className="btn-magic btn-ghost text-sm">
            <FolderCog className="w-4 h-4" /> {t("teacher.rename_class")}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-[#A5A0D6]" />
          <input data-testid="students-search" className="input-magic ps-9 !w-56" placeholder={t("teacher.search_student")}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {selected.size > 0 && (
          <>
            <span data-testid="bulk-selected-count" className="text-sm font-extrabold text-[#27C9EE]">
              {t("teacher.selected_count", { n: selected.size })}
            </span>
            <button data-testid="bulk-award-btn" onClick={() => open("award", [...selected])}
              className="btn-magic text-sm">
              <PlusCircle className="w-4 h-4" /> {t("teacher.add_points")}
            </button>
            <button data-testid="bulk-deduct-btn" onClick={() => open("bulk-deduct", [...selected])}
              className="btn-magic btn-pink text-sm">
              <MinusCircle className="w-4 h-4" /> {t("teacher.deduct_points")}
            </button>
            <button data-testid="clear-selection-btn" onClick={clearSelection}
              className="btn-magic btn-ghost text-sm">
              {t("teacher.clear_selection")}
            </button>
          </>
        )}
        {students?.some((student) => student.is_active) && (
          <button data-testid="select-all-visible-btn" onClick={selectVisible}
            className="btn-magic btn-ghost text-sm">
            {t("teacher.select_all")}
          </button>
        )}
        <button data-testid="add-student-btn" onClick={() => open("student")} className="btn-magic btn-gold text-sm">
          <UserPlus className="w-4 h-4" /> {t("teacher.add_student")}
        </button>
        <button data-testid="add-multiple-students-btn" onClick={() => open("bulk-students")}
          className="btn-magic btn-gold text-sm">
          <UsersRound className="w-4 h-4" /> {t("teacher.add_multiple_students")}
        </button>
        <button data-testid="import-students-btn" onClick={() => open("student-import")}
          className="btn-magic btn-cyan text-sm">
          <FileUp className="w-4 h-4" /> {t("teacher.import_students")}
        </button>
      </div>

      {tempPw && (
        <div data-testid="reset-pw-banner" className="rounded-2xl bg-[#FFC83D]/12 border border-[#FFC83D]/40 p-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-bold">{tempPw.name} — {t("teacher.temp_password")}: <span className="text-[#FFC83D] font-black" dir="ltr">{tempPw.pw}</span></p>
          <button data-testid="reset-pw-dismiss" onClick={() => setTempPw(null)} className="text-xs font-extrabold text-[#A5A0D6] hover:text-white transition-colors">{t("common.close")}</button>
        </div>
      )}

      {!students ? (
        <SkeletonPanel lines={6} />
      ) : students.length === 0 ? (
        <EmptyState icon="Users" title={t("teacher.no_students")} testId="students-empty" />
      ) : (
        <div className="grid gap-3">
          {students.map((s) => (
            <div key={s.id} data-testid={`student-row-${studentTestKey(s)}`}
              className={`magic-panel magic-panel-hover p-4 flex flex-wrap items-center gap-4 border ${selected.has(s.id) ? "!border-[#9C42F5]/60" : ""} ${!s.is_active ? "opacity-55" : ""}`}>
              <button onClick={() => s.is_active && toggle(s.id)}
                data-testid={`select-student-${studentTestKey(s)}`}
                disabled={!s.is_active}
                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center text-xs font-black shrink-0 transition-colors ${selected.has(s.id) ? "bg-[#9C42F5] border-[#9C42F5]" : "border-white/25"} ${!s.is_active ? "cursor-not-allowed" : ""}`}
                aria-label={pick(s, "name")}>
                {selected.has(s.id) ? "✓" : ""}
              </button>
              <button onClick={() => navigate(`/profile/${s.id}`)}
                data-testid={`view-student-${studentTestKey(s)}`}
                className="flex items-center gap-3 flex-1 min-w-[180px] group">
                <Avatar src={s.avatar} name={pick(s, "name")} size="md" />
                <div className="text-start">
                  <p className="font-extrabold group-hover:text-[#FFC83D] transition-colors">
                    {pick(s, "name")}
                    {!s.is_active && <span className="ms-2 text-[10px] text-[#FF8FB1] font-black">({t("teacher.disabled_tag")})</span>}
                  </p>
                  {s.login_enabled ? (
                    <p className="text-[11px] text-[#A5A0D6] font-bold" dir="ltr">
                      @{s.username}
                    </p>
                  ) : (
                    <p data-testid={`profile-only-${studentTestKey(s)}`}
                      className="text-[11px] text-[#A5A0D6] font-bold">
                      {t("teacher.profile_only")}
                    </p>
                  )}
                </div>
              </button>
              <div className="text-center px-2">
                <p className="text-lg font-black text-[#48D276]">{s.total_points}</p>
                <p className="text-[10px] text-[#A5A0D6] font-bold">{wsLabel(config?.ui_labels, "points", lang, t("dash.points_label"))}</p>
              </div>
              <div className="text-center px-2 min-w-[90px]">
                <p className="text-sm font-extrabold" style={{ color: s.level?.color || "#A5A0D6" }}>{s.level ? pick(s.level, "name") : "—"}</p>
                <p className="text-[10px] text-[#A5A0D6] font-bold">{t("teacher.level")}</p>
              </div>
              <div className="text-center px-2">
                <p className="text-lg font-black text-[#FF63B5]">{s.badge_count}</p>
                <p className="text-[10px] text-[#A5A0D6] font-bold">{t("dash.badges_count")}</p>
              </div>
              <div className="text-center px-2 hidden lg:block">
                <p className="text-xs font-bold text-[#D8D4F2]">{s.last_activity ? fmtRelative(s.last_activity, lang) : "—"}</p>
                <p className="text-[10px] text-[#A5A0D6] font-bold">{t("teacher.last_activity")}</p>
              </div>
              <div className="flex gap-1.5 ms-auto flex-wrap">
                <button data-testid={`award-${studentTestKey(s)}`} onClick={() => open("award", [s.id])} title={t("teacher.add_points")}
                  className="w-10 h-10 rounded-xl bg-[#48D276]/15 border border-[#48D276]/40 text-[#48D276] flex items-center justify-center hover:bg-[#48D276]/30 transition-colors">
                  <PlusCircle className="w-5 h-5" />
                </button>
                <button data-testid={`deduct-${studentTestKey(s)}`} onClick={() => open("deduct", [s.id])} title={t("teacher.deduct_points")}
                  className="w-10 h-10 rounded-xl bg-[#EF4D9A]/15 border border-[#EF4D9A]/40 text-[#FF8FB1] flex items-center justify-center hover:bg-[#EF4D9A]/30 transition-colors">
                  <MinusCircle className="w-5 h-5" />
                </button>
                <button data-testid={`badge-${studentTestKey(s)}`} onClick={() => open("badge", [s.id])} title={t("teacher.award_badge")}
                  className="w-10 h-10 rounded-xl bg-[#FFC83D]/15 border border-[#FFC83D]/40 text-[#FFC83D] flex items-center justify-center hover:bg-[#FFC83D]/30 transition-colors">
                  <Award className="w-5 h-5" />
                </button>
                <button data-testid={`edit-${studentTestKey(s)}`} onClick={() => { setEditingStudent(s); setModal("edit"); }} title={t("teacher.edit_student")}
                  className="w-10 h-10 rounded-xl bg-[#268CEB]/15 border border-[#268CEB]/40 text-[#27C9EE] flex items-center justify-center hover:bg-[#268CEB]/30 transition-colors">
                  <Pencil className="w-5 h-5" />
                </button>
                {s.login_enabled ? (
                  <>
                    <button data-testid={`resetpw-${studentTestKey(s)}`} onClick={() => resetPw(s)}
                      title={t("teacher.reset_password")}
                      className="w-10 h-10 rounded-xl bg-[#9C42F5]/15 border border-[#9C42F5]/40
                        text-[#B06EF7] flex items-center justify-center hover:bg-[#9C42F5]/30
                        transition-colors">
                      <KeyRound className="w-5 h-5" />
                    </button>
                    <button data-testid={`toggle-login-${studentTestKey(s)}`} onClick={() => toggleLogin(s)}
                      title={s.login_active ? t("teacher.disable_login") : t("teacher.reactivate_login")}
                      className={`w-10 h-10 rounded-xl border flex items-center justify-center
                        transition-colors ${s.login_active
                          ? "bg-[#FFAE32]/12 border-[#FFAE32]/40 text-[#FFC83D] hover:bg-[#FFAE32]/25"
                          : "bg-[#48D276]/10 border-[#48D276]/40 text-[#48D276] hover:bg-[#48D276]/25"}`}>
                      {s.login_active ? <Ban className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                    </button>
                  </>
                ) : (
                  <button data-testid={`enable-login-${studentTestKey(s)}`}
                    onClick={() => setLoginStudent(s)} title={t("teacher.enable_login")}
                    className="w-10 h-10 rounded-xl bg-[#9C42F5]/15 border border-[#9C42F5]/40
                      text-[#B06EF7] flex items-center justify-center hover:bg-[#9C42F5]/30
                      transition-colors">
                    <KeyRound className="w-5 h-5" />
                  </button>
                )}
                <button data-testid={`delete-${studentTestKey(s)}`} onClick={() => setDeletingStudent(s)} title={t("teacher.delete_student")}
                  className="w-10 h-10 rounded-xl bg-[#F43F5E]/12 border border-[#F43F5E]/45 text-[#FB7185] flex items-center justify-center hover:bg-[#F43F5E]/30 transition-colors">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AwardPointsModal open={modal === "award"} onClose={() => setModal(null)} students={students || []}
        categories={config?.categories || []} presetIds={preset} onDone={load}
        key={`award-${preset.join()}`} action="add" />
      <AwardPointsModal open={modal === "bulk-deduct"} onClose={() => setModal(null)}
        students={students || []} categories={config?.categories || []} presetIds={preset} onDone={load}
        key={`bulk-deduct-${preset.join()}`} action="deduct" />
      <DeductPointsModal open={modal === "deduct"} onClose={() => setModal(null)} students={students || []}
        presetId={preset[0] || null} onDone={load} key={`deduct-${preset.join()}`} />
      <AwardBadgeModal open={modal === "badge"} onClose={() => setModal(null)} students={students || []}
        presetId={preset[0] || null} onDone={load} key={`badge-${preset.join()}`} />
      <AddStudentModal open={modal === "student"} onClose={() => setModal(null)} onDone={() => { load(); loadClasses(); }} classes={classes} />
      <BulkAddStudentsModal open={modal === "bulk-students"} onClose={() => setModal(null)}
        onDone={() => { load(); loadClasses(); }} classes={classes} />
      <StudentImportModal open={modal === "student-import"} onClose={() => setModal(null)}
        onDone={() => { load(); loadClasses(); }} classes={classes} />
      <EnableStudentLoginModal open={!!loginStudent} onClose={() => setLoginStudent(null)}
        student={loginStudent} onDone={load} />
      <ClassFormModal open={modal === "class"} onClose={() => setModal(null)} editing={editingClass}
        onDone={() => { loadClasses(); }} />
      <EditStudentModal open={modal === "edit"} onClose={() => setModal(null)} student={editingStudent}
        classes={classes} onDone={load} />
      <DeleteStudentModal open={!!deletingStudent} onClose={() => setDeletingStudent(null)}
        student={deletingStudent} onDone={load} />
    </div>
  );
}
